import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import GameRelatedList from "./GameRelatedList";
import { SonicTermData } from "../../common/classes/SonicTerm";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getPersonData,
  getPerson,
  getPersonTotalEntries,
} from "../../redux/reducers/person";

import {
  AuthStatus,
  getAuthStatus,
  getAuthToken,
} from "../../redux/reducers/auth";

import { useGetTeams } from "../../utils/useTeam";
import { useGetProject } from "../../utils/useProject";
import { useGetEvents } from "../../utils/useEvent";
import { useGetTopics } from "../../utils/useTopics";

type Props = {
  defaultOptions: Array<SonicTermData>;
  modalOptions: Array<SonicTermData>;
  modalLoading: boolean;
  handleSearchOption: (keyword: string) => void;
};
const GameRelatedListContainer: React.FC<Props> = ({
  defaultOptions,
  modalOptions,
  modalLoading,
  handleSearchOption,
}) => {
  const dispatch = useAppDispatch();
  const auth_status = useAppSelector(getAuthStatus);
  const auth_token = useAppSelector(getAuthToken);
  const profiles = useAppSelector(getPersonData);
  const profileEntries = useAppSelector(getPersonTotalEntries);

  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    items: { teams, total_entries: teamEntries },
    isLoading: teamLoading,
  } = useGetTeams({
    page: 1,
    per: 10,
    games: id,
  });

  const {
    items: { projects, total_entries: projectEntries },
    isLoading: projectLoading,
  } = useGetProject(
    {
      auth_token,
      page: 1,
      per: 10,
      games: id,
    },
    auth_status !== AuthStatus.Loading
  );

  const {
    items: { events, total_entries: eventEntries },
    isLoading: eventLoading,
  } = useGetEvents({ page: 1, per: 10, games: id });

  const {
    topicsData: { items: topics, total_entries: topicsEntries },
    isLoading: topicsLoading,
  } = useGetTopics({ page: 1, per: 10, games: id });

  useEffect(() => {
    if (auth_status === AuthStatus.Loading) return;

    if (id) {
      if (defaultOptions.find((game) => game.id === id) === undefined) {
        return setIsLoading(false);
      }

      setIsLoading(true);

      const personPromise = dispatch(
        getPerson({ page: 1, per: 10, games: [id] })
      );

      const getGameRelatedList = async () => {
        await Promise.all([personPromise]);
      };

      const promise = getGameRelatedList();
      promise.then(() => {
        setIsLoading(false);
      });

      return () => {
        personPromise.abort();
      };
    }
  }, [auth_status, id]);

  const navigate = useNavigate();

  const handleSearch = (game: SonicTermData) => {
    navigate(`/games/${game.id}`);
  };

  if (defaultOptions.find((game) => game.id === id) === undefined) {
    return <Navigate to="/404" replace />;
  }

  return (
    <GameRelatedList
      modalLoading={modalLoading}
      modalOptions={modalOptions}
      handleSearchOption={handleSearchOption}
      selectedGame={defaultOptions.find((game) => game.id === id)}
      handleSearch={handleSearch}
      isLoading={
        isLoading ||
        teamLoading ||
        projectLoading ||
        eventLoading ||
        topicsLoading
      }
      profiles={profiles?.length > 0 ? profiles : []}
      teams={teams}
      projects={projects}
      events={events}
      topics={topics}
      profileEntries={isLoading ? 0 : profileEntries}
      teamEntries={teamLoading ? 0 : teamEntries}
      projectEntries={projectLoading ? 0 : projectEntries}
      eventEntries={eventLoading ? 0 : eventEntries}
      topicsEntries={topicsLoading ? 0 : topicsEntries}
    />
  );
};

export default GameRelatedListContainer;
